import type {
  Alarm,
  AppState,
  DayTemplate,
  DoneState,
  ISODate,
  ISOInstant,
  LocalEvent,
  OccurrenceKey,
  Overlay,
  PlainDateTimeStr,
  RecurrenceRule,
  Timing,
} from './model.ts';
import { sha256Text } from './hash.ts';
import { sanitizeUrl } from './url.ts';
import { Temporal, addDays, compareDates, daysBetween, startDay, timingInstants, startOfIsoWeek, resolveLocal, NonexistentLocalTimeError } from './time.ts';
import { moveTimingToDate, moveTimingToStart, resizeTiming } from './timing.ts';
import { expandOccurrences, isOccurrenceOf, validateRecurrence } from './recurrence.ts';

// ---------------------------------------------------------------------------
// Fel som UI och API visar som de är – en misslyckad åtgärd får aldrig se lyckad ut.
// ---------------------------------------------------------------------------

export class SourceDateLockedError extends Error {
  readonly code = 'SOURCE_DATE_LOCKED';
  constructor() {
    super('Källdatum från SkatteFix. Planera arbetet separat.');
    this.name = 'SourceDateLockedError';
  }
}
export class NotFoundError extends Error {
  readonly code = 'NOT_FOUND';
  constructor(what: string) {
    super(`${what} hittades inte.`);
    this.name = 'NotFoundError';
  }
}
export class ValidationError extends Error {
  readonly code = 'VALIDATION';
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}
export class UndoConflictError extends Error {
  readonly code = 'UNDO_CONFLICT';
  constructor() {
    super('Aktiviteten har ändrats efter åtgärden och kan inte ångras automatiskt.');
    this.name = 'UndoConflictError';
  }
}

// ---------------------------------------------------------------------------
// Ändringsmängder och ångra
// ---------------------------------------------------------------------------

export interface Change {
  events?: Record<string, LocalEvent | null>;
  overlays?: Record<string, Overlay | null>;
  templates?: Record<string, DayTemplate | null>;
}

export interface UndoEntry {
  opId: string;
  label: string;
  /** Värden före åtgärden. */
  before: Change;
  /** Revisioner efter åtgärden – ångra vägrar om något ändrats sedan dess. */
  after: { events: Record<string, number | null>; overlays: Record<string, number | null> };
}

export interface OpResult {
  state: AppState;
  change: Change;
  undo: UndoEntry | null;
  /** True om opId redan var tillämpad (dubbelklick/omförsök) – inget nytt skapades. */
  duplicate: boolean;
  /** Tidsjustering p.g.a. sommartidsglapp. */
  adjusted?: boolean;
}

export function applyChange(state: AppState, change: Change): AppState {
  const next = { ...state };
  if (change.events) {
    next.events = { ...state.events };
    for (const [id, ev] of Object.entries(change.events)) {
      if (ev === null) delete next.events[id];
      else next.events[id] = ev;
    }
  }
  if (change.overlays) {
    next.overlays = { ...state.overlays };
    for (const [k, ov] of Object.entries(change.overlays)) {
      if (ov === null) delete next.overlays[k];
      else next.overlays[k] = ov;
    }
  }
  if (change.templates) {
    next.templates = { ...state.templates };
    for (const [k, t] of Object.entries(change.templates)) {
      if (t === null) delete next.templates[k];
      else next.templates[k] = t;
    }
  }
  return next;
}

export function commit(state: AppState, opId: string, label: string, change: Change, now: Temporal.Instant): OpResult {
  if (state.appliedOps[opId]) return { state, change: {}, undo: null, duplicate: true };
  const before: Change = {};
  const after: UndoEntry['after'] = { events: {}, overlays: {} };
  if (change.events) {
    before.events = {};
    for (const [id, ev] of Object.entries(change.events)) {
      before.events[id] = state.events[id] ?? null;
      after.events[id] = ev ? ev.revision : null;
    }
  }
  if (change.overlays) {
    before.overlays = {};
    for (const [k, ov] of Object.entries(change.overlays)) {
      before.overlays[k] = state.overlays[k] ?? null;
      after.overlays[k] = ov ? ov.revision : null;
    }
  }
  if (change.templates) {
    before.templates = {};
    for (const k of Object.keys(change.templates)) before.templates[k] = state.templates[k] ?? null;
  }
  const next = applyChange(state, change);
  next.appliedOps = { ...state.appliedOps, [opId]: iso(now) };
  return { state: next, change, undo: { opId, label, before, after }, duplicate: false };
}

/**
 * Ångra en hel operation (t.ex. kopiering till tre dagar) som en enhet. Återställda poster får ny
 * revision (så synk ser ångringen som en ny ändring, inte en tidsresa).
 */
export function undo(state: AppState, entry: UndoEntry, now: Temporal.Instant): { state: AppState; change: Change } {
  for (const [id, rev] of Object.entries(entry.after.events)) {
    const cur = state.events[id];
    const curRev = cur && !cur.deleted ? cur.revision : cur?.deleted ? cur.revision : null;
    if ((rev ?? null) !== (curRev ?? null)) throw new UndoConflictError();
  }
  for (const [k, rev] of Object.entries(entry.after.overlays)) {
    if ((rev ?? null) !== (state.overlays[k]?.revision ?? null)) throw new UndoConflictError();
  }
  const change: Change = {};
  const nowIso = iso(now);
  if (entry.before.events) {
    change.events = {};
    for (const [id, prev] of Object.entries(entry.before.events)) {
      const cur = state.events[id];
      if (prev === null) {
        // Skapades av operationen → tombstone (synkbar radering).
        change.events[id] = cur ? { ...cur, deleted: true, revision: cur.revision + 1, updatedAt: nowIso } : null;
      } else {
        change.events[id] = { ...prev, revision: (cur?.revision ?? prev.revision) + 1, updatedAt: nowIso };
      }
    }
  }
  if (entry.before.overlays) {
    change.overlays = {};
    for (const [k, prev] of Object.entries(entry.before.overlays)) {
      const cur = state.overlays[k];
      change.overlays[k] = prev ? { ...prev, revision: (cur?.revision ?? prev.revision) + 1 } : cur ? { ...cur, done: null, notes: '', revision: cur.revision + 1 } : null;
    }
  }
  if (entry.before.templates) change.templates = { ...entry.before.templates };
  return { state: applyChange(state, change), change };
}

// ---------------------------------------------------------------------------
// Hjälpare
// ---------------------------------------------------------------------------

export const iso = (i: Temporal.Instant): ISOInstant => i.toString({ smallestUnit: 'second' });

/** Deterministiska ID:n från opId → ett omförsök med samma opId kan aldrig skapa dubbletter. */
export function derivedId(prefix: string, opId: string, index: number | string): string {
  return `${prefix}_${sha256Text(`${opId}#${index}`).slice(0, 20)}`;
}
export function exportUidFor(id: string): string {
  return `${id}@autoday.local`;
}

export function isSourceKey(ref: string): boolean {
  return ref.includes('|');
}

function getEvent(state: AppState, id: string): LocalEvent {
  const ev = state.events[id];
  if (!ev || ev.deleted) {
    if (isSourceKey(id) || state.sourceItems[id]) throw new SourceDateLockedError();
    throw new NotFoundError('Aktiviteten');
  }
  return ev;
}

function bump(ev: LocalEvent, now: Temporal.Instant, patch: Partial<LocalEvent>): LocalEvent {
  return { ...ev, ...patch, revision: ev.revision + 1, updatedAt: iso(now) };
}

function validateTiming(t: Timing): void {
  if (t.kind === 'allDay') {
    if (daysBetween(t.start, t.endExclusive) < 1) throw new ValidationError('Slutdatum måste vara efter startdatum.');
    return;
  }
  if (Temporal.PlainDateTime.compare(Temporal.PlainDateTime.from(t.end), Temporal.PlainDateTime.from(t.start)) < 0) {
    throw new ValidationError('Sluttiden är före starttiden.');
  }
}

function sanitizeAlarms(alarms: Alarm[]): Alarm[] {
  if (alarms.length > 10) throw new ValidationError('Högst 10 påminnelser per aktivitet.');
  return alarms.map((a) => ({ ...a, origin: 'user' as const, action: 'DISPLAY' as const }));
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export interface EventInput {
  calendarId: string;
  title: string;
  categoryId: string;
  icon: LocalEvent['icon'];
  timing: Timing;
  location?: string;
  notes?: string;
  url?: string;
  alarms: Alarm[];
  recurrence?: RecurrenceRule;
  priority?: 'normal' | 'high';
  linkedSourceKey?: string;
}

function checkInput(state: AppState, input: EventInput): { url?: string } {
  if (!input.title.trim()) throw new ValidationError('Titel saknas.');
  if (input.title.length > 300) throw new ValidationError('Titeln är för lång.');
  const cal = state.calendars[input.calendarId];
  if (!cal) throw new ValidationError('Okänd kalender.');
  if (cal.kind === 'import') throw new ValidationError('Importerade kalendrar är skrivskyddade. Välj Privat eller Arbete.');
  if (!state.categories[input.categoryId]) throw new ValidationError('Okänd kategori.');
  validateTiming(input.timing);
  if (input.timing.kind === 'timed') {
    // Ny tid som användaren skrivit in: ett klockslag som inte finns måste väljas om explicit.
    try {
      resolveLocal(input.timing.start, input.timing.timeZone, 'reject');
    } catch (e) {
      if (e instanceof NonexistentLocalTimeError) throw new ValidationError(e.message + ' Välj en annan tid.');
      throw e;
    }
  }
  if (input.recurrence) {
    const err = validateRecurrence(input.recurrence);
    if (err) throw new ValidationError(err);
  }
  if (input.url) {
    const u = sanitizeUrl(input.url);
    if (!u.url) throw new ValidationError('Länken måste börja med https:// eller http://.');
    return { url: u.url };
  }
  return {};
}

export function createEvent(state: AppState, opId: string, input: EventInput, now: Temporal.Instant): OpResult & { id: string } {
  const { url } = checkInput(state, input);
  const id = derivedId('ev', opId, 0);
  const ev: LocalEvent = {
    id,
    calendarId: input.calendarId,
    title: input.title.trim(),
    categoryId: input.categoryId,
    icon: input.icon,
    timing: input.timing,
    ...(input.location ? { location: input.location } : {}),
    ...(input.notes ? { notes: input.notes } : {}),
    ...(url ? { url } : {}),
    alarms: sanitizeAlarms(input.alarms),
    ...(input.recurrence ? { recurrence: input.recurrence } : {}),
    ...(input.priority ? { priority: input.priority } : {}),
    ...(input.linkedSourceKey ? { linkedSourceKey: input.linkedSourceKey } : {}),
    done: null,
    exportUid: exportUidFor(id),
    revision: 1,
    createdAt: iso(now),
    updatedAt: iso(now),
  };
  return { ...commit(state, opId, `Skapa "${ev.title}"`, { events: { [id]: ev } }, now), id };
}

export function updateEvent(state: AppState, opId: string, id: string, input: EventInput, now: Temporal.Instant, expectedRevision?: number): OpResult {
  const ev = getEvent(state, id);
  if (expectedRevision !== undefined && ev.revision !== expectedRevision) throw new UndoConflictError();
  const { url } = checkInput(state, input);
  const next = bump(ev, now, {
    calendarId: input.calendarId,
    title: input.title.trim(),
    categoryId: input.categoryId,
    icon: input.icon,
    timing: input.timing,
    location: input.location || undefined,
    notes: input.notes || undefined,
    url: url || undefined,
    alarms: sanitizeAlarms(input.alarms),
    recurrence: input.recurrence,
    priority: input.priority,
  });
  return commit(state, opId, `Ändra "${next.title}"`, { events: { [id]: next } }, now);
}

export function deleteEvent(state: AppState, opId: string, id: string, now: Temporal.Instant): OpResult {
  const ev = getEvent(state, id);
  return commit(state, opId, `Ta bort "${ev.title}"`, { events: { [id]: bump(ev, now, { deleted: true }) } }, now);
}

/** Ta bort en förekomst av en serie. */
export function deleteOccurrence(state: AppState, opId: string, id: string, key: OccurrenceKey, now: Temporal.Instant): OpResult {
  const ev = getEvent(state, id);
  if (!ev.recurrence) return deleteEvent(state, opId, id, now);
  return commit(state, opId, `Ta bort en förekomst av "${ev.title}"`, { events: { [id]: bump(ev, now, { exdates: [...new Set([...(ev.exdates ?? []), key])] }) } }, now);
}

// ---------------------------------------------------------------------------
// Flytta (drag-and-drop och "Flytta till datum" går genom samma funktion)
// ---------------------------------------------------------------------------

export type MoveTarget = { toDate: ISODate } | { toStart: PlainDateTimeStr } | { resizeEnd: PlainDateTimeStr | ISODate };

/**
 * Flyttar en egen aktivitet. Importerade källposter (nyckel "källa|uid|…") avvisas alltid med
 * SourceDateLockedError – regeln finns här i domänlagret och används även av API:t.
 * scope 'occurrence' flyttar en förekomst av en serie; 'series' flyttar hela serien.
 */
export function moveEvent(
  state: AppState,
  opId: string,
  ref: string,
  target: MoveTarget,
  now: Temporal.Instant,
  opts: { scope?: 'series' | 'occurrence'; occurrenceKey?: OccurrenceKey } = {},
): OpResult {
  if (isSourceKey(ref) || state.sourceItems[ref]) throw new SourceDateLockedError();
  const ev = getEvent(state, ref);
  const occKey = opts.occurrenceKey;
  if (ev.recurrence && opts.scope === 'occurrence') {
    if (!occKey || !isOccurrenceOf(ev, occKey)) throw new ValidationError('Förekomsten finns inte.');
    const occ = expandOccurrences(ev, occKey, addDays(occKey, 1), ev.timing.kind === 'timed' ? ev.timing.timeZone : 'UTC').find((o) => o.key === occKey);
    const baseTiming = ev.overrides?.[occKey]?.timing ?? occ?.timing ?? moveTimingToDate(ev.timing, occKey).timing;
    const r = applyTarget(baseTiming, target);
    const overrides = { ...(ev.overrides ?? {}), [occKey]: { ...(ev.overrides?.[occKey] ?? {}), timing: r.timing } };
    const res = commit(state, opId, `Flytta "${ev.title}" (en förekomst)`, { events: { [ev.id]: bump(ev, now, { overrides }) } }, now);
    return { ...res, adjusted: r.adjusted };
  }
  const r = applyTarget(ev.timing, target);
  const res = commit(state, opId, `Flytta "${ev.title}"`, { events: { [ev.id]: bump(ev, now, { timing: r.timing }) } }, now);
  return { ...res, adjusted: r.adjusted };
}

function applyTarget(t: Timing, target: MoveTarget) {
  if ('toDate' in target) return moveTimingToDate(t, target.toDate);
  if ('toStart' in target) return moveTimingToStart(t, target.toStart);
  return resizeTiming(t, target.resizeEnd);
}

/** Domänregel för UI: kan posten dras? */
export function canDrag(state: AppState, ref: string): boolean {
  if (isSourceKey(ref) || state.sourceItems[ref]) return false;
  const ev = state.events[ref];
  return !!ev && !ev.deleted;
}

// ---------------------------------------------------------------------------
// Klar-status (lokal kvittering – aldrig inlämningskvitto)
// ---------------------------------------------------------------------------

export function setEventDone(state: AppState, opId: string, id: string, done: boolean, now: Temporal.Instant, occurrenceKey?: OccurrenceKey): OpResult {
  const ev = getEvent(state, id);
  const ds: DoneState = { at: iso(now) };
  if (ev.recurrence) {
    if (!occurrenceKey) throw new ValidationError('Ange förekomst.');
    const doneOccurrences = { ...(ev.doneOccurrences ?? {}) };
    if (done) doneOccurrences[occurrenceKey] = ds;
    else delete doneOccurrences[occurrenceKey];
    return commit(state, opId, done ? `Klar: "${ev.title}"` : `Återöppna "${ev.title}"`, { events: { [id]: bump(ev, now, { doneOccurrences }) } }, now);
  }
  return commit(state, opId, done ? `Klar: "${ev.title}"` : `Återöppna "${ev.title}"`, { events: { [id]: bump(ev, now, { done: done ? ds : null }) } }, now);
}

/**
 * Importerad skyldighet: användaren måste bekräfta att åtgärden kontrollerats. Länkar och passerade
 * datum markerar aldrig något som klart.
 */
export function setSourceDone(state: AppState, opId: string, key: string, done: boolean, now: Temporal.Instant, confirmChecked = false): OpResult {
  const item = state.sourceItems[key];
  if (!item) throw new NotFoundError('Den importerade posten');
  if (done && !confirmChecked) throw new ValidationError('Bekräfta att du har kontrollerat att åtgärden är utförd.');
  const ov = state.overlays[key] ?? { key, revision: 0 };
  const next: Overlay = {
    ...ov,
    done: done ? { at: iso(now), confirmedChecked: true } : null,
    // Bekräftad klar efter granskning löser granskningen; återöppning behåller den.
    review: done ? null : ov.review ?? null,
    revision: ov.revision + 1,
  };
  return commit(state, opId, done ? `Klar här: "${item.data.title}"` : `Återöppnad: "${item.data.title}"`, { overlays: { [key]: next } }, now);
}

export function setSourceNotes(state: AppState, opId: string, key: string, notes: string, now: Temporal.Instant): OpResult {
  if (!state.sourceItems[key]) throw new NotFoundError('Den importerade posten');
  const ov = state.overlays[key] ?? { key, revision: 0 };
  return commit(state, opId, 'Ändra anteckning', { overlays: { [key]: { ...ov, notes: notes.slice(0, 20000), revision: ov.revision + 1 } } }, now);
}

/** Granskning: användaren har sett källändringen/saknaden. */
export function resolveSourceReview(state: AppState, opId: string, key: string, now: Temporal.Instant, action: 'keepDone' | 'reopen' | 'acknowledge'): OpResult {
  const ov = state.overlays[key];
  if (!ov?.review) throw new ValidationError('Inget att granska.');
  const next: Overlay = {
    ...ov,
    review: null,
    done: action === 'reopen' ? null : ov.done ?? null,
    revision: ov.revision + 1,
  };
  return commit(state, opId, 'Granskning klar', { overlays: { [key]: next } }, now);
}

// ---------------------------------------------------------------------------
// Länkad arbetsaktivitet ("Förbered moms") – flytt av den rör aldrig källan
// ---------------------------------------------------------------------------

export function createLinkedWork(
  state: AppState,
  opId: string,
  key: string,
  input: { title: string; date: ISODate; startTime?: string; durationMinutes?: number; calendarId?: string; notes?: string },
  now: Temporal.Instant,
): OpResult & { id: string } {
  const item = state.sourceItems[key];
  if (!item) throw new NotFoundError('Den importerade posten');
  const tz = state.settings.timeZone;
  const timing: Timing = input.startTime
    ? {
        kind: 'timed',
        start: `${input.date}T${input.startTime}`,
        end: Temporal.PlainDateTime.from(`${input.date}T${input.startTime}`).add({ minutes: input.durationMinutes ?? 60 }).toString({ smallestUnit: 'minute' }),
        timeZone: tz,
        durationPolicy: 'wall',
      }
    : { kind: 'allDay', start: input.date, endExclusive: addDays(input.date, 1) };
  return createEvent(
    state,
    opId,
    {
      calendarId: input.calendarId ?? 'cal-arbete',
      title: input.title,
      categoryId: 'arbete',
      icon: 'briefcase',
      timing,
      notes: input.notes ?? `Förberedelse inför: ${item.data.summary}`,
      alarms: input.startTime ? [{ id: 'a1', kind: 'relative', offsetMinutes: -60, related: 'start', action: 'DISPLAY', origin: 'user' }] : [],
      linkedSourceKey: key,
    },
    now,
  );
}

// ---------------------------------------------------------------------------
// Kopiera dag / vecka / mall – ett gemensamt kodflöde
// ---------------------------------------------------------------------------

export interface CopyCandidate {
  /** Källa: lokal aktivitet + förekomst, eller mallrad. */
  sourceRef: string;
  occurrenceKey?: OccurrenceKey;
  title: string;
  timing: Timing;
  template: Omit<LocalEvent, 'id' | 'exportUid' | 'revision' | 'createdAt' | 'updatedAt' | 'done' | 'doneOccurrences' | 'timing' | 'recurrence' | 'exdates' | 'overrides'>;
  /** Dag i källintervallet (0 = första dagen). */
  dayOffset: number;
}

export interface CopyPlanItem {
  newEvent: LocalEvent;
  candidateIndex: number;
  targetStart: ISODate;
  conflicts: Array<{ id: string; title: string }>;
  adjusted: boolean;
}

export interface CopyPlan {
  opId: string;
  items: CopyPlanItem[];
  /** Importerade deadlines i källintervallet – kopieras aldrig. */
  excludedImported: Array<{ key: string; title: string; date: ISODate }>;
  conflictCount: number;
}

/** Aktiviteter som STARTAR i [fromDate, fromDate+days) – en flerdagshändelse tas därför bara en gång. */
export function copyCandidates(state: AppState, fromDate: ISODate, days: number): { candidates: CopyCandidate[]; excludedImported: CopyPlan['excludedImported'] } {
  const tz = state.settings.timeZone;
  const toEx = addDays(fromDate, days);
  const candidates: CopyCandidate[] = [];
  for (const ev of Object.values(state.events)) {
    if (ev.deleted) continue;
    for (const occ of expandOccurrences(ev, addDays(fromDate, -7), toEx, tz)) {
      const sd = startDay(occ.timing, tz);
      if (compareDates(sd, fromDate) < 0 || compareDates(sd, toEx) >= 0) continue;
      const { id: _i, exportUid: _u, revision: _r, createdAt: _c, updatedAt: _up, done: _d, doneOccurrences: _do, timing: _t, recurrence: _rr, exdates: _ex, overrides: _ov, ...rest } = ev;
      candidates.push({
        sourceRef: ev.id,
        ...(ev.recurrence ? { occurrenceKey: occ.key } : {}),
        title: occ.override?.title ?? ev.title,
        timing: occ.timing,
        template: rest,
        dayOffset: daysBetween(fromDate, sd),
      });
    }
  }
  const excludedImported: CopyPlan['excludedImported'] = [];
  for (const s of Object.values(state.sourceItems)) {
    if (s.archived) continue;
    for (const occ of expandOccurrences(s.data, fromDate, toEx, tz)) {
      excludedImported.push({ key: s.key, title: s.data.summary, date: startDay(occ.timing, tz) });
    }
  }
  candidates.sort((a, b) => a.dayOffset - b.dayOffset || a.title.localeCompare(b.title, 'sv'));
  return { candidates, excludedImported };
}

/** Omräkna personliga påminnelser: absoluta larm görs om till samma avstånd från den nya starten. */
function recomputeAlarms(alarms: Alarm[], oldTiming: Timing, tz: string): Alarm[] {
  const oldStart = timingInstants(oldTiming, tz).start;
  return alarms
    .filter((a) => a.origin === 'user')
    .map((a, i) => {
      if (a.kind === 'relative') return { ...a, id: `a${i}` };
      const offsetMinutes = Math.round(oldStart.until(Temporal.Instant.from(a.at), { largestUnit: 'hours' }).total({ unit: 'minutes' }));
      return { id: `a${i}`, kind: 'relative' as const, offsetMinutes, related: 'start' as const, action: 'DISPLAY' as const, origin: 'user' as const };
    });
}

function overlaps(a: Timing, b: Timing, tz: string): boolean {
  if (a.kind === 'allDay' || b.kind === 'allDay') return false; // Heldagar blockerar inte tid i krockkontroll.
  const x = timingInstants(a, tz);
  const y = timingInstants(b, tz);
  return Temporal.Instant.compare(x.start, y.end) < 0 && Temporal.Instant.compare(y.start, x.end) < 0;
}

/**
 * Planerar en kopiering. selected = index i candidates. targetStarts = måldag (dagskopia) eller
 * målveckans måndag (veckokopia). Befintliga aktiviteter på måldagar ersätts aldrig.
 */
export function planCopy(state: AppState, opId: string, candidates: CopyCandidate[], selected: number[], targetStarts: ISODate[], now: Temporal.Instant): CopyPlan {
  const tz = state.settings.timeZone;
  const items: CopyPlanItem[] = [];
  const uniqueTargets = [...new Set(targetStarts)].sort();
  let n = 0;
  for (const target of uniqueTargets) {
    for (const ci of [...new Set(selected)].sort((a, b) => a - b)) {
      const c = candidates[ci];
      if (!c) continue;
      const newDate = addDays(target, c.dayOffset);
      const moved = moveTimingToDate(c.timing, newDate);
      const id = derivedId('ev', opId, n++);
      const ev: LocalEvent = {
        ...c.template,
        id,
        title: c.title,
        timing: moved.timing,
        alarms: recomputeAlarms(c.template.alarms, c.timing, tz),
        done: null,
        exportUid: exportUidFor(id),
        copiedFromId: c.sourceRef,
        revision: 1,
        createdAt: iso(now),
        updatedAt: iso(now),
      };
      delete (ev as Partial<LocalEvent>).linkedSourceKey;
      const conflicts: CopyPlanItem['conflicts'] = [];
      for (const other of Object.values(state.events)) {
        if (other.deleted) continue;
        for (const occ of expandOccurrences(other, addDays(newDate, -1), addDays(newDate, 2), tz)) {
          if (overlaps(occ.timing, ev.timing, tz)) conflicts.push({ id: other.id, title: other.title });
        }
      }
      items.push({ newEvent: ev, candidateIndex: ci, targetStart: target, conflicts, adjusted: moved.adjusted });
    }
  }
  return { opId, items, excludedImported: [], conflictCount: items.reduce((s, i) => s + (i.conflicts.length ? 1 : 0), 0) };
}

/** Atomisk: alla kopior eller inga, ångras som en operation. Samma opId två gånger skapar inget nytt. */
export function applyCopy(state: AppState, plan: CopyPlan, now: Temporal.Instant, label: string): OpResult {
  if (plan.items.length > 500) throw new ValidationError('För många kopior på en gång (max 500).');
  const events: Record<string, LocalEvent> = {};
  for (const it of plan.items) events[it.newEvent.id] = it.newEvent;
  return commit(state, plan.opId, label, { events }, now);
}

// ---------------------------------------------------------------------------
// Mallar
// ---------------------------------------------------------------------------

export function saveTemplate(state: AppState, opId: string, name: string, span: 'day' | 'week', fromDate: ISODate, now: Temporal.Instant): OpResult & { id: string } {
  const start = span === 'week' ? startOfIsoWeek(fromDate) : fromDate;
  const { candidates } = copyCandidates(state, start, span === 'week' ? 7 : 1);
  if (!candidates.length) throw new ValidationError('Inga egna aktiviteter att spara som mall.');
  const id = derivedId('tpl', opId, 0);
  const tpl: DayTemplate = {
    id,
    name: name.trim() || (span === 'week' ? 'Veckomall' : 'Dagmall'),
    span,
    items: candidates.map((c) => ({ ...c.template, title: c.title, timing: c.timing, dayOffset: c.dayOffset, alarms: recomputeAlarms(c.template.alarms, c.timing, state.settings.timeZone) })),
    createdAt: iso(now),
  };
  return { ...commit(state, opId, `Spara mall "${tpl.name}"`, { templates: { [id]: tpl } }, now), id };
}

export function templateCandidates(tpl: DayTemplate): CopyCandidate[] {
  return tpl.items.map((it, i) => {
    const { dayOffset, timing, title, ...rest } = it;
    return { sourceRef: `${tpl.id}#${i}`, title, timing, template: { ...rest, title } as CopyCandidate['template'], dayOffset };
  });
}
